import React, { useState, useEffect, useRef } from 'react';
import { string } from 'prop-types';
import { nanoid } from 'nanoid';
import parse from 'html-react-parser';

import useFetch from '../../react-hooks/use-fetch';
import useQueryString from '../../react-hooks/use-query-string';
import { stringifyUrl, getQueryStringValue } from '../../utils/url';
import Facets from '../facets';
import Search from '../search';
import List from '../search/list';
import Pagination from '../pagination';
import { triggerCustomEvent } from '../../utils/events';
import { createFocusTrap } from 'focus-trap';

const mobileButtonId = `id-${nanoid()}`;
const contentId = `id-${nanoid()}`;

function TodaySearchResultsListing({
  clearFiltersLabel,
  filtersAriaLabel,
  resultsAriaLabel,
  facetsSearchLabel,
  goToPageLabel,
  errorDescription,
  errorHeading,
  errorNoResults,
  fetchUrl,
  heading,
  keywordQueryString,
  loadingDataLabel,
  narrowResultsHeading,
  pageQueryString,
  paginationLabel,
  searchButtonLabel,
  searchFacetPlaceholder,
  searchId,
  searchLabel,
  showAllFacetsLabel,
  showLessFacetsLabel
}) {
  const filtersRef = useRef(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [keywordQs, setKeywordQs] = useQueryString(keywordQueryString);
  const [pageQs, setPageQs] = useQueryString(pageQueryString);
  const [facetsQs, setFacetsQs] = useState({});
  const [filtersFocusTrap, setFiltersFocusTrap] = useState();
  const query = {
    ...(keywordQs && { [keywordQueryString]: keywordQs }),
    ...(pageQs && { [pageQueryString]: pageQs }),
    ...facetsQs
  };
  const fetchUrlWithParams = stringifyUrl({
    url: fetchUrl,
    query
  });
  const { response, error, isLoading } = useFetch(fetchUrlWithParams);
  const filtersSelected = Object.entries(query).some(
    ([k, v]) => v && k !== keywordQueryString
  );
  const resultListing = response?.data?.ResultListing ?? [];

  function clearFilters() {
    setPageQs('');
    setKeywordQs('');
    setFacetsQs({});
    triggerCustomEvent(window, 'clearFacets');
  }

  function updateFacets(keyword) {
    setFacetsQs((prev) => ({
      ...prev,
      [keyword]: getQueryStringValue(keyword)
    }));
    setPageQs('');
    setFiltersExpanded(false);
  }

  function onSearch(keyword) {
    setKeywordQs(keyword);
    setPageQs('');
    setFiltersExpanded(false);
  }

  function onPageChange(page) {
    setPageQs(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function onFiltersExpanded() {
    const filtersFocusTrap = createFocusTrap(filtersRef.current, {
      escapeDeactivates: true,
      clickOutsideDeactivates: true,
      onActivate: () => {
        setFiltersExpanded(true);
      },
      onDeactivate: () => {
        setFiltersExpanded(false);
      }
    });
    setFiltersFocusTrap(filtersFocusTrap);
    filtersFocusTrap.activate();

    setFiltersExpanded(!filtersExpanded);
  }

  useEffect(() => {
    if (filtersExpanded && filtersRef) {
      const { scrollHeight } = filtersRef.current;
      const windowHeight = window.document.documentElement.clientHeight;

      if (scrollHeight > windowHeight) {
        filtersRef.current.scrollTop = windowHeight - scrollHeight;
      }
    }
  }, [filtersExpanded, filtersRef]);

  useEffect(() => {
    const body = window.document.body;

    if (filtersExpanded) {
      body.classList.add('noscroll');
    } else {
      body.classList.remove('noscroll');
    }
  }, [filtersExpanded]);

  return (
    <div className="faculty-staff-listing">
      {heading && <h1>{heading}</h1>}
      <div className="faculty-staff-listing__wrapper">
        <section
          aria-label={filtersAriaLabel}
          className={`faculty-staff-listing__filters ${
            filtersExpanded ? 'faculty-staff-listing__filters--expanded' : ''
          }`}
          ref={filtersRef}
        >
          <h3
            className={`faculty-staff-listing__filters-mobile-header ${
              filtersExpanded
                ? 'faculty-staff-listing__filters-mobile-header--expanded'
                : ''
            }`}
          >
            <button
              aria-expanded={filtersExpanded}
              className="faculty-staff-listing__filters-mobile-header-button"
              onClick={onFiltersExpanded}
              id={mobileButtonId}
              aria-controls={contentId}
            >
              <span>
                {filtersExpanded && response
                  ? response.data?.Results
                  : narrowResultsHeading}
              </span>
              <svg>
                <use xlinkHref="#chevron-down-outline" />
              </svg>
            </button>
          </h3>
          <div
            className="faculty-staff-listing__filters-content"
            role="region"
            id={contentId}
            aria-labelledby={mobileButtonId}
          >
            <div className="faculty-staff-listing__filters-content-header">
              <h3 className="faculty-staff-listing__narrow-results-heading">
                {narrowResultsHeading}
              </h3>
              {(filtersSelected || keywordQs) && (
                <button className="btn-label" onClick={clearFilters}>
                  <span>{clearFiltersLabel}</span>
                </button>
              )}
            </div>
            <Search
              buttonLabel={searchButtonLabel}
              id={searchId}
              label={searchLabel}
              onSearch={onSearch}
              value={keywordQs}
            />
            {response?.data?.Facets?.length > 0 &&
              response.data.Facets.map(({ Name, Keyword, Options }) => {
                const length = Options.length;

                return (
                  <Facets
                    key={Keyword}
                    keyword={Keyword}
                    heading={`${Name} ${length > 10 ? `(${length})` : ''}`}
                    facets={Options}
                    searchFacetPlaceholder={searchFacetPlaceholder}
                    searchFacetLabel={facetsSearchLabel.replace(
                      '{{category}}',
                      Name
                    )}
                    showAllLabel={showAllFacetsLabel}
                    showLessLabel={showLessFacetsLabel}
                    updateFacets={updateFacets}
                  />
                );
              })}
            <div className="faculty-staff-listing__clear-filters-mobile">
              {filtersSelected && (
                <button className="btn-label" onClick={clearFilters}>
                  <span>{clearFiltersLabel}</span>
                </button>
              )}
            </div>
          </div>
        </section>
        <div className="faculty-staff-listing__list">
          {!isLoading && !error && resultListing.length === 0 && (
            <div className="rte">{parse(errorNoResults)}</div>
          )}
          <List
            resultsAriaLabel={resultsAriaLabel}
            fetchUrl={fetchUrlWithParams}
            errorDescription={errorDescription}
            errorHeading={errorHeading}
            loadingDataLabel={loadingDataLabel}
            resultListing={resultListing}
            resultText={response?.data?.Results}
            error={error}
            isLoading={isLoading}
          />
          {!isLoading && resultListing.length !== 0 && (
            <Pagination
              label={paginationLabel}
              currentPage={response.data.CurrentPage}
              goToPageLabel={goToPageLabel}
              onPageChange={onPageChange}
              totalPages={response.data.TotalPages}
            />
          )}
        </div>
      </div>
    </div>
  );
}

TodaySearchResultsListing.propTypes = {
  clearFiltersLabel: string.isRequired,
  facetsSearchLabel: string.isRequired,
  filtersAriaLabel: string.isRequired,
  goToPageLabel: string.isRequired,
  errorDescription: string.isRequired,
  errorHeading: string.isRequired,
  errorNoResults: string.isRequired,
  fetchUrl: string.isRequired,
  heading: string,
  keywordQueryString: string.isRequired,
  loadingDataLabel: string.isRequired,
  narrowResultsHeading: string.isRequired,
  pageQueryString: string.isRequired,
  paginationLabel: string.isRequired,
  resultsAriaLabel: string.isRequired,
  searchButtonLabel: string.isRequired,
  searchFacetPlaceholder: string.isRequired,
  searchId: string.isRequired,
  searchLabel: string.isRequired,
  showAllFacetsLabel: string.isRequired,
  showLessFacetsLabel: string.isRequired
};

export default TodaySearchResultsListing;
